import React from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const easeOut = [0.16, 1, 0.3, 1];

// Positions roughly on a Mercator-style world map (percentages)
const hubCoords = [
  { top: "52%", left: "22%" }, // Villahermosa, Mexico
  { top: "50%", left: "24%" }, // Ciudad del Carmen
  { top: "45%", left: "22.5%" }, // Houston
  { top: "45%", left: "23.5%" }, // Monterrey
];

const extraMarkers = [
  { top: "43%", left: "52%", label: "Europe" },
  { top: "55%", left: "62%", label: "Middle East" },
  { top: "62%", left: "82%", label: "Asia" },
];

export default function GlobalPresence() {
  const { t } = useLang();

  return (
    <section
      id="presence"
      data-testid="presence-section"
      className="relative py-24 sm:py-32 bg-[#0A0A0B] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{t.presence.eyebrow}</span>
            </div>
            <h2 className="font-display font-bold text-[clamp(2rem,4vw,3.5rem)] text-white tracking-tight leading-[1.05] max-w-[20ch]">
              {t.presence.title}
            </h2>
          </div>
          <p className="text-[#A1A1AA] max-w-md text-base leading-relaxed">
            {t.presence.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: easeOut }}
            className="lg:col-span-8 relative bg-[#0F0F11] border border-white/5 aspect-[16/9] overflow-hidden"
          >
            <div className="absolute inset-0 blueprint-bg opacity-40" />
            {/* stylized world map silhouette via SVG */}
            <svg
              viewBox="0 0 1000 500"
              className="absolute inset-0 w-full h-full opacity-30"
              aria-hidden
            >
              <path
                fill="#D4AF37"
                fillOpacity="0.1"
                stroke="#D4AF37"
                strokeWidth="0.4"
                d="M150,180 Q180,150 220,160 L260,170 L290,200 L270,230 L240,250 L200,260 L170,240 Z M330,120 Q380,100 430,110 L470,130 L500,160 L490,200 L460,220 L420,230 L380,220 L340,200 L320,170 Z M520,150 L570,140 L620,150 L640,180 L630,210 L590,220 L550,210 L520,180 Z M660,180 Q700,160 740,170 L780,190 L800,220 L780,250 L740,260 L700,250 L670,230 L650,210 Z M820,250 Q860,240 900,250 L930,270 L940,300 L920,320 L880,330 L840,320 L810,300 L800,280 Z"
              />
            </svg>

            {/* connection lines from Mexico hubs to international markers */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              preserveAspectRatio="none"
              viewBox="0 0 100 100"
            >
              {extraMarkers.map((m, i) => (
                <motion.line
                  key={i}
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.4 }}
                  transition={{ duration: 1.5, delay: 0.3 + i * 0.2 }}
                  viewport={{ once: true }}
                  x1="22.5"
                  y1="45"
                  x2={parseFloat(m.left)}
                  y2={parseFloat(m.top)}
                  stroke="#D4AF37"
                  strokeWidth="0.15"
                  strokeDasharray="1 1"
                />
              ))}
            </svg>

            {/* primary hub markers */}
            {hubCoords.map((c, i) => (
              <div
                key={i}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ top: c.top, left: c.left }}
                data-testid={`map-marker-${i}`}
              >
                <span className="absolute inset-0 -m-2 w-5 h-5 rounded-full bg-[#D4AF37]/30 animate-ping" />
                <span className="relative block w-2.5 h-2.5 rounded-full bg-[#D4AF37] ring-2 ring-[#D4AF37]/40" />
              </div>
            ))}

            {/* extra region markers */}
            {extraMarkers.map((m, i) => (
              <div
                key={i}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ top: m.top, left: m.left }}
              >
                <div className="relative flex items-center gap-1.5">
                  <span className="block w-1.5 h-1.5 bg-[#8B939C]" />
                  <span className="text-[9px] uppercase tracking-[0.2em] text-[#8B939C]">
                    {m.label}
                  </span>
                </div>
              </div>
            ))}

            {/* compass */}
            <div className="absolute top-4 right-4 text-[#8B939C] text-[10px] uppercase tracking-[0.3em] flex items-center gap-2">
              <span>N</span>
              <span className="h-4 w-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37]">40.5°</span>
            </div>
            <div className="absolute bottom-4 left-4 text-[#8B939C] text-[10px] uppercase tracking-[0.3em]">
              Operational Map · v2.4
            </div>
          </motion.div>

          {/* Hubs list */}
          <div className="lg:col-span-4 flex flex-col gap-3">
            {t.presence.hubs.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: easeOut }}
                data-testid={`hub-${i}`}
                className="relative p-5 border border-white/5 bg-[#0F0F11] hover:border-[#D4AF37]/30 transition-colors duration-300 group"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-white text-lg tracking-tight">
                      {h.city}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1">
                      {h.country}
                    </div>
                    <div className="text-xs text-[#8B939C] mt-2 leading-relaxed">
                      {h.role}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
